<template>
    <form
        class="app-publishing-edit-form grid"
        @submit.prevent="submit()"
        autocomplete="off">
        <div class="app-publishing-edit-form__header col-12">
            <div class="app-publishing-edit-form__header-titles">
                <div class="app-tab-navigation">
                    <div class="app-tab-navigation__breadcrumbs">
                        <a class="app-tab-navigation__breadcrumb" :href="`/projects/${project.id}/publications`">
                            <svg class="element-icon--breadcrumb element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-arrow-left"></use></svg>
                            Back to all courses
                        </a>
                    </div>
                </div>
                <h3 v-if="isCreate" class="app-publishing-edit-form__header-title">Create New Course</h3>
                <h3 v-else class="app-publishing-edit-form__header-title">Edit Course: {{ publication.name }} </h3>
            </div>
            <div class="app-publishing-edit-form__header-buttons">
                <app-button
                    theme="primary"
                    type="submit"
                    :disabled="!requirementsMet || disabled">
                    {{ disabled ? 'Saving...' : 'Save Course' }}
                </app-button>
                <app-button v-if="publication.id && !isCreate"
                    class="element-button--secondary element-button is--alert"
                    @click="remove()">
                    Delete Course
                </app-button>
                <app-button v-if="publication.id && isCreate"
                    theme="secondary"
                    class="is--alert"
                    @click="cancel()">
                    Cancel
                </app-button>
                <span v-if="isClosed">
                    <app-button
                        @click="close()"
                        theme="secondary">
                        Close
                    </app-button>
                </span>
                <span v-if="isCreate">
                    <app-button
                        @click="reset()"
                        theme="secondary">
                        Reset
                    </app-button>
                </span>
            </div>
        </div>
        <div class="page-main__section col-12 col-md-4 col-lg-3">
            <div class="page-main__section-header">
                <h4 class="page-main__section-title">Course Details</h4>
                <p class="element-instructions">Enter course details below.</p>
            </div>
            <fieldset class="input text" :disabled="disabled">
                <label for="name">Course Title</label>
                <input type="text" name="name" placeholder="i.e. Introduction to Fundamentals" v-model="publication.name" required>
            </fieldset>
            <fieldset class="input select" :disabled="disabled">
                <label for="menu_type">Course Menu Layout</label>
                <select name="menu_type" v-model="publication.menu_type">
                    <option v-for="menu in availableMenus" :key="`menu-${menu.interaction}`" :value="menu.interaction">
                        {{ menu.name }}
                    </option>
                </select>
            </fieldset>
            <fieldset class="input textarea" :disabled="disabled">
                <label for="description">Description</label>
                <textarea name="description" rows="3" v-model="publication.description"></textarea>
            </fieldset>
            <fieldset class="col-12" :disabled="disabled" v-if="hasSchema">
                <div class="checkbox">
                    <input type="checkbox" class="checkbox__input" id="advanced-mode-toggle" v-model="advancedMode">
                    <label class="custom-control-label" for="advanced-mode-toggle">
                        <span>Advanced Editing</span>
                    </label>
                </div>
            </fieldset>
        </div>
	<div v-show="loading && !advancedMode" class="loader">
            <img src="/img/loader.gif" alt="Loading...">
        </div>
        <div class="page-main__section col-12 col-md-8 col-lg-9" v-if="advancedMode">
            <div class="page-main__section-header">
                <h4 class="page-main__section-title">Advanced Editing</h4>
                <p class="element-instructions">Adjust the JSON below. Note: this should only be used by those familiar with code to avoid breaking the screen.</p>
            </div>
            <code-editor
                :value="currentContentString"
                language="json"
                @change="onContentChange"
                rows="40">
            </code-editor>
        </div>
        <div class="page-main__section col-12 col-md-8 col-lg-9" v-else-if="hasSchema">
            <div class="page-main__section-header">
                <h4 class="page-main__section-title">Course Menu Structure</h4>
                <p class="element-instructions">Build the menu for this course below.</p>
            </div>
            <content-builder-form-editor
                ref="editor"
                class="menu-editor"
                :key="`${source.id}-${formRenderIndex}`"
                :content="currentContent"
                :schemaKey="schemaKey"
                @ready="loading = false"
                @change="onContentChange">
            </content-builder-form-editor>
        </div>
        <fieldset v-else class="page-main__section col-12 col-md-8 col-lg-9" :disabled="disabled">
            <label class="page-main__section-title" for="topics">Course Structure</label>
                <p class="element-instructions">Build the structure for this course below.</p>
            <!-- use VueDraggable here to sort topics and learning objects -->

            <div class="app-vertical-navigation">
                <Draggable
                    v-model="publication.topics"
                    handle=".topic-handle"
                    draggable=".topic"
                    class="app-publishing-edit-form__topics"
                    :disabled="disabled"
                    :group="{ name: 'topics' }">
                    <div v-for="(topic, tIndex) in publication.topics"
                        :key="`topic-${tIndex}`"
                        class="app-publishing-edit-form__topic topic">

                        <div class="app-vertical-navigation__menu-item">
                            <svg class="topic-handle element-icon--draggable element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-draggable"></use></svg>
                            <div class="input text">
                                <div
                                    contenteditable="true"
                                    title="Topic Title"
                                    class="input"
                                    spellcheck="false"
                                    @blur="topicChanged($event, tIndex)"
                                    v-text="publication.topics[tIndex].name">
                                </div>
                            </div>
                            <!-- <input type="text" class="form-control" name="topic" id="topic" placeholder="i.e. Topic A" v-model="publication.topics[tIndex].name"> -->
                            <!-- topics can only be removed if they have no objects in them -->
                            <!-- minimum at least one topic -->
                            <app-button
                                class="element-button--icon element-button"
                                theme="clear"
                                :disabled="publication.topics.length === 1"
                                @click="removeTopic(tIndex)">
                                <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                            </app-button>
                        </div>
                        <Draggable
                            v-model="topic.content"
                            handle=".object-handle"
                            draggable=".object"
                            :disabled="disabled"
                        class="app-publishing-edit-form__objects"   
                            :group="{ name: 'objects' }">
                            <div v-for="(object, cIndex) in topic.content"
                                class="app-publishing-edit-form__object object app-vertical-navigation__submenu-item"
                                :key="`object-${tIndex}-${cIndex}`">
                                <svg class="object-handle element-icon--draggable element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-draggable"></use></svg>
                                
                                <p class="app-vertical-navigation__submenu-item-title">{{ object.name }}</p>

                                <app-button
                                    class="element-button--icon element-button"
                                    theme="clear"
                                    @click="removeObject(tIndex, cIndex)">
                                    <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                                </app-button>
                            </div>

                            <div slot="footer" class="app-publishing-edit-form__add-object-select app-vertical-navigation__submenu-item">
                                <app-select
                                    ref="app-select"
                                    placeholder="Add a Learning Object..."
                                    :options="selectOptions"
                                    @update:modelValue="addObjectToTopic(tIndex, $event)">
                                </app-select>
                            </div>
                        </Draggable>
                    </div>

                    <div slot="footer" class="app-publishing-edit-form__add-topic">
                        <app-button
                            theme="secondary"
                            @click="addTopic()">
                            Add Topic
                        </app-button>
                    </div>
                </Draggable>
            </div>
        </fieldset>
    </form>
</template>

<script>
import _ from 'lodash'
import ContentBuilderFormEditor from './ContentBuilderFormEditor.vue'
import { getInteractionId } from '../helpers/builder'
import api from '../api/publisher'
import { VueDraggableNext as Draggable } from 'vue-draggable-next'

export default {
    components: {
        ContentBuilderFormEditor,
        Draggable
    },
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        source: {
            type: Object,
            default: () => ({
                id: 'new-publication',
                name: '',
                description: ''
            })
        },
        isClosed: {
            type: Boolean,
            default: false
        },
        isCreate: {
            type: Boolean,
            default: false
        },
    },
    data () {
        const startingTopic = this.generateNewTopic()
        const clonedSource = _.cloneDeep(this.source)
        return {
            formRenderIndex: 0,
            menu: clonedSource.menu,
            loading: false,
            publication: {
                // enforce minimum single topic
                topics: [startingTopic],
                menu_type: this.getDefaultMenu(),
                ...clonedSource
            }
        }
    },
    computed: {
        project () {
            return this.$store.state.builder.source
        },
        driver () {
            return this.project?.driver
        },
        requirementsMet () {
            return this.publication.name.length && this.objectsIncluded.length
        },
        objectsIncluded () {
            return _.reduce(this.publication.topics, (result, topic) => {
                result = _.concat(result, topic.content)
                return result
            }, [])
        },
        selectOptions () {
            return {
                ajax: {
                    url: `/projects/${this.project.id}/learning`,
                    dataType: 'json',
                    delay: 250,
                    data: (params) => ({
                        query: params.term, // search term
                        page: params.page,
                        exclude: _.map(this.objectsIncluded, 'id'),
                        limit: 100
                    }),
                    processResults: this.processResults,
                    cache: true
                },
                minimumInputLength: 0
            }
        },
        availableMenus () {
           const menus = _.get(this.driver, 'menus', [])
           return _.map(menus, (menu) => {
               return {
                   ...menu,
                   interaction: getInteractionId(menu)
               }
           })
        },
        hasSchema () {
            let _hasSchema = [null, 'none'].indexOf(this.publication.menu_type) === -1;

            if (_hasSchema !== false && this.schemaKey)
            {
                this.loading = true
                return true
            }

            return false
        },
        currentContent () {
            if (this.menu && this.hasSchema) {
                return this.menu.data
            }

            return {
                topics: _.map(this.publication.topics, (topic) => {
                    return {
                        $_name: topic.name,
                        content: topic.content.map((object) => {
                            return object.id
                        })
                    }
                })
            }
        },
        currentContentString () {
            return JSON.stringify(this.currentContent, null, 2)
        },
        selectedMenuType () {
            this.loading = true
            return _.find(this.availableMenus, { interaction: this.publication.menu_type })
        },
        schemaKey () {
            return this.selectedMenuType?.$ref
        },
        advancedMode: {
            get () {
                return this.$store.state.builder.advancedMode
            },
            set (value) {
                this.$store.commit('builder/setAdvancedMode', value)
            }
        }
    },
    watch: {
        advancedMode (value) {
            if (!value) {
                this.formRenderIndex++
            }
        }
    },
    methods: {
        generateNewTopic () {
            const count = _.get(this.publication, 'topics.length', 0)
            return { name: `Topic ${count + 1}`, content: [] }
        },
        getDefaultMenu () {
            const driver = this.driver ?? this.$attrs['driver'] // this.driver is not immediatelly available until store is set, allow for fallback
            if (driver?.category === 'phoenix')
                return 'menu'
            return null
        },
        addTopic () {
            const newTopic = this.generateNewTopic()
            this.publication.topics.push(newTopic)
        },
        removeTopic (index) {
            this.publication.topics.splice(index, 1)
        },
        topicChanged (e, tIndex) {
            const title = e.target.textContent || e.target.innerText
            this.publication.topics[tIndex].name = title;
        },
        addObject (object) {
            const topic = _.last(this.publication.topics)
            topic.content.push(object)
        },
        addObjectToTopic (tIndex, object) {
            this.publication.topics[tIndex].content.push(object)
            this.$refs['app-select'][tIndex].clearSelection()
        },
        removeObject (tIndex, cIndex) {
            this.publication.topics[tIndex].content.splice(cIndex, 1)
        },
        processResults (response, { page = 1 }) {
            const data = _.get(response, 'data.data', [])
            const results = _.map(data, (object) => {
                return {
                    id: object.id,
                    text: object.name,
                    value: object
                }
            })

            return {
                results,
                pagination: {
                    more: (page * data.per_page) < data.total
                }
            }
        },
        close () {
            this.$emit('close')
        },
        remove () {
            this.$emit('remove', this.publication)
        },
        reset () {
           location.reload();
        },
        cancel () {
            window.location.href = '/projects/'+this.project.id+'/publications'
        },
        async submit () {
            // update existing pages
            console.info('Saving page data...')

            const data = _.pick(this.publication, ['id', 'description', 'name', 'menu_type', 'configuration', 'topics'])

            // save menu (if opened/changed)
            if (this.hasSchema) {
                const sourceMenu = this.menu || { }
                const dataObject = {
                    ...sourceMenu,
                    data: this.$refs.editor.getValue(),
                    name: this.selectedMenuType?.type,
                    title: this.selectedMenuType?.name,
                    type: this.selectedMenuType?.type,
                    schema_ref: this.selectedMenuType?.$ref
                }

                data.menu = dataObject
            }

            // save publication
            if (this.publication.id == 'new-publication') {
                this.$emit('save', data)
            } else {
                this.$emit('update', data)
            }
            
        },
        onContentChange (data) {
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data)
                } catch (err) {
                    alert('JSON syntax invalid, please fix the error and try again.')
                    return
                }
            }

            if (data?.topics) {
                this.publication.topics = _.map(data.topics, (topic) => {
                    return {
                        name: topic.$_name,
                        content: topic.content.map(({ id, $_name }) => {
                            return { id, name: $_name }
                        })
                    }
                })
            }

            if (this.menu)
                this.menu.data = data
        }
    }
}
</script>
